import { Plane, Hotel, MapPin, Star, Clock, DollarSign, Check } from 'lucide-react';

interface SearchResultCardProps {
  type: 'flight' | 'hotel' | 'activity';
  title: string;
  subtitle: string;
  price?: string | number;
  currency?: string;
  details: string[];
  image?: string;
  rating?: number;
  amenities?: string[];
  onSelect?: () => void;
  selectLabel?: string;
  selected?: boolean;
}

export default function SearchResultCard({
  type,
  title,
  subtitle,
  price,
  currency,
  details,
  image,
  rating,
  amenities,
  onSelect,
  selectLabel = 'Select',
  selected,
}: SearchResultCardProps) {
  const getIcon = () => {
    switch (type) {
      case 'flight':
        return <Plane className="h-5 w-5" />;
      case 'hotel':
        return <Hotel className="h-5 w-5" />;
      case 'activity':
        return <MapPin className="h-5 w-5" />;
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'flight':
        return 'bg-blue-100 text-blue-600';
      case 'hotel':
        return 'bg-purple-100 text-purple-600';
      case 'activity':
        return 'bg-green-100 text-green-600';
    }
  };

  const formatPrice = (price?: string | number, currency?: string) => {
    if (!price) return null;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return null;
    const displayCurrency = currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numPrice);
  };

  return (
    <div
      className={`bg-white rounded-xl p-4 shadow-sm border transition-all duration-200 ${
        selected
          ? 'border-primary-500 ring-2 ring-primary-100'
          : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
      }`}
    >
      <div className="flex gap-4">
        {/* Icon/Image */}
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconColor()}`}
        >
          {image ? (
            <img src={image} alt={title} className="w-full h-full object-cover rounded-lg" />
          ) : (
            getIcon()
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-gray-900 truncate">{title}</h4>
              <p className="text-sm text-gray-600 truncate">{subtitle}</p>
            </div>
            {price && (
              <div className="text-right flex-shrink-0">
                <span className="text-lg font-bold text-gray-900">
                  {formatPrice(price, currency)}
                </span>
                {type === 'hotel' && (
                  <span className="text-xs text-gray-500">/night</span>
                )}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-wrap gap-2 mt-2">
            {details.map((detail, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full"
              >
                {detail}
              </span>
            ))}
          </div>

          {/* Rating & Amenities */}
          {(rating || amenities) && (
            <div className="mt-2">
              {rating && (
                <div className="inline-flex items-center gap-1 text-sm text-yellow-600 mr-2">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="font-medium">{rating}</span>
                </div>
              )}
              {amenities && amenities.length > 0 && (
                <div className="inline-flex gap-1">
                  {amenities.slice(0, 3).map((amenity, index) => (
                    <span
                      key={index}
                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                    >
                      {amenity}
                    </span>
                  ))}
                  {amenities.length > 3 && (
                    <span className="text-xs text-gray-400">+{amenities.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        {onSelect && (
          <button
            onClick={onSelect}
            className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              selected
                ? 'bg-primary-600 text-white'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            }`}
          >
            {selected ? (
              <span className="flex items-center gap-1">
                <Check className="h-4 w-4" />
                Selected
              </span>
            ) : (
              selectLabel
            )}
          </button>
        )}
      </div>
    </div>
  );
}
